import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'react-feather';
import { toast } from 'react-toastify';
import {
  TelephonyCallDetail,
  callDetailIsRedFlagged,
  telephonyUserApi,
} from '../../../Telephony/telephonyApi';
import RecordingPlayer from '../../../Telephony/RecordingPlayer';
import { Page, PageHeader, Card, Button, Badge, Table, DescriptionList, Loader, EmptyState } from '../../../ui';
import { CallStatusBadge } from './callStatus';
import './telephony.scss';

const fmt = (iso: string | null | undefined): string =>
  iso ? new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

/**
 * Telephony → Call detail: everything about one call — metadata, both legs,
 * and every recording with its transcript and (if flagged) the red-flag
 * reason. Linked from the "View" button on the call-logs listing.
 */
const TelephonyCallDetailPage: React.FC = () => {
  const { callId } = useParams<{ callId: string }>();
  const navigate = useNavigate();
  const [call, setCall] = useState<TelephonyCallDetail | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!callId) return;
    setLoading(true);
    // As the signed-in user: the platform 403s a non-manager opening someone else's call.
    telephonyUserApi<TelephonyCallDetail>(`/v1/calls/${callId}`)
      .then(setCall)
      .catch((e) => toast.error(`Call detail: ${e.message}`))
      .finally(() => setLoading(false));
  }, [callId]);

  return (
    <Page>
      <PageHeader
        eyebrow="Telephony"
        title="Call detail"
        subtitle={call ? <code>{call.id}</code> : undefined}
        actions={
          <Button
            variant="secondary"
            icon={<ArrowLeft size={16} aria-hidden />}
            onClick={() => navigate(`${process.env.PUBLIC_URL}/pages/telephony-logs`)}
          >
            Back to logs
          </Button>
        }
      />

      {loading ? (
        <Loader label="Loading call…" />
      ) : !call ? (
        <EmptyState title="Call not found" description="It may have been removed, or you may not have access to it." />
      ) : (
        <>
          {callDetailIsRedFlagged(call) ? (
            <Card className="tel-flag-banner" title={<><span aria-hidden>🚩</span> Flagged for review</>}>
              {call.recordings.find((r) => r.sentimentFlag)?.sentimentReason ||
                'This call was flagged during automated review.'}
            </Card>
          ) : null}

          <Card title="Summary" actions={<CallStatusBadge status={call.status} />}>
            <DescriptionList
              items={[
                {
                  label: 'Direction',
                  value: (
                    <>
                      {call.direction === 'INBOUND' ? 'Incoming' : 'Outgoing'}
                      {call.channel === 'WHATSAPP' ? ' (WhatsApp)' : ''}
                    </>
                  ),
                },
                {
                  label: 'Agent',
                  value: (
                    <>
                      {call.agent?.name ?? '—'}
                      {call.agent?.externalId ? (
                        <span className="tel-sub"><code>{call.agent.externalId}</code></span>
                      ) : null}
                    </>
                  ),
                },
                { label: 'Team', value: call.team?.name ?? '—' },
                { label: 'Customer ID', value: call.customerUserId ? <code>{call.customerUserId}</code> : '—' },
                {
                  label: 'From → To',
                  value:
                    call.direction === 'INBOUND'
                      ? `${call.customerE164} → ${call.callerIdE164}`
                      : `${call.callerIdE164} → ${call.customerE164}`,
                },
                { label: 'Vendor', value: call.provider },
                {
                  label: 'Source',
                  value: (
                    <>
                      {call.source}
                      {call.sourceRecordId ? <span className="tel-sub">{call.sourceRecordId}</span> : null}
                    </>
                  ),
                },
                { label: 'Disposition', value: call.disposition ?? '—' },
                { label: 'Started', value: fmt(call.createdAt) },
                { label: 'Bridged', value: fmt(call.bridgedAt) },
                { label: 'Ended', value: fmt(call.endedAt) },
                { label: 'Talk time', value: call.talkSeconds != null ? `${call.talkSeconds}s` : '—' },
                { label: 'Hangup cause', value: call.hangupCause ?? '—' },
                { label: 'Ended by', value: call.disconnectedBy ? call.disconnectedBy.toLowerCase() : '—' },
                { label: 'Cost', value: call.costAmount ? `${call.costAmount} ${call.costCurrency ?? ''}` : '—' },
              ]}
            />
          </Card>

          {call.legs.length > 0 ? (
            <Card flush title="Legs">
              <Table caption="Call legs">
                <thead>
                  <tr>
                    <th>Role</th><th>Number</th><th>Status</th>
                    <th>Ringing</th><th>Answered</th><th>Ended</th>
                    <th className="ds-num">Duration</th><th>Hangup cause</th>
                  </tr>
                </thead>
                <tbody>
                  {call.legs.map((leg) => (
                    <tr key={leg.id}>
                      <td>{leg.role}</td>
                      <td>{leg.e164}</td>
                      <td>{leg.status}</td>
                      <td className="text-nowrap">{fmt(leg.ringingAt)}</td>
                      <td className="text-nowrap">{fmt(leg.answeredAt)}</td>
                      <td className="text-nowrap">{fmt(leg.endedAt)}</td>
                      <td className="ds-num">{leg.durationSec != null ? `${leg.durationSec}s` : '—'}</td>
                      <td>{leg.hangupCause ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </Card>
          ) : null}

          {call.recordings.length === 0 ? (
            <EmptyState title="No recording for this call" />
          ) : (
            call.recordings.map((rec) => (
              <Card
                key={rec.id}
                title="Recording"
                subtitle={rec.durationSec != null ? `${rec.durationSec}s` : undefined}
                actions={rec.sentimentFlag ? <Badge tone="danger">🚩 Flagged</Badge> : undefined}
              >
                <div className="tel-recording">
                  {rec.status === 'STORED' ? (
                    <RecordingPlayer recordingId={rec.id} />
                  ) : (
                    <div className="ds-text-muted">{rec.status.toLowerCase()}</div>
                  )}

                  <div className="ds-stack">
                    <span className="tel-sub">Transcript</span>
                    {rec.transcriptStatus === 'STORED' ? (
                      rec.transcript ? (
                        <pre className="tel-transcript">{rec.transcript}</pre>
                      ) : (
                        <div className="ds-text-muted">Recording too short to transcribe</div>
                      )
                    ) : rec.transcriptStatus === 'FAILED' ? (
                      <div className="ds-text-muted">Transcription failed</div>
                    ) : (
                      <div className="ds-text-muted">Transcription pending</div>
                    )}
                  </div>
                </div>
              </Card>
            ))
          )}
        </>
      )}
    </Page>
  );
};

export default TelephonyCallDetailPage;
